"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useOnboarding } from "@/onboarding/OnboardingContext";
import { 
  Check, 
  X, 
  HelpCircle, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Shield,
  ArrowRight
} from "lucide-react";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { PRIVACY_ACT_CATEGORIES } from "@/app/pages/privacy-act/constants/questions";
import { 
  completePrivacyActSOA, 
  SOAAnswer, 
  QUESTION_TO_CONTROL_MAP,
  fetchPAControls,
  PAControl
} from "@/services/privacyActSOAService";

type AnswerType = 'yes' | 'no' | 'unsure' | null;

export default function ModalControlQuestionsExample() {
  const { closeActive, goBack, setStatus, openStep } = useOnboarding();
  const [open, setOpen] = React.useState(true);
  const [answers, setAnswers] = React.useState<Record<string, AnswerType>>({});
  const [controls, setControls] = React.useState<Record<string, PAControl>>({});
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<{ controlsApplicable: number; controlsNotApplicable: number; tasksCreated: number; tasksSkipped: number } | null>(null);

  // Fetch controls on mount
  React.useEffect(() => {
    if (open) {
      setLoading(true);
      fetchPAControls()
        .then(setControls)
        .catch(err => {
          console.error('Error fetching controls:', err);
          setError('Failed to load Privacy Act controls');
        })
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleAnswer = (questionId: string, answer: AnswerType) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const getAnswerIcon = (answer: AnswerType) => {
    switch (answer) {
      case 'yes':
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case 'no':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'unsure':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default:
        return null;
    }
  };

  const answeredCount = Object.values(answers).filter(a => a !== null).length;
  const totalCount = PRIVACY_ACT_CATEGORIES.length;
  const progress = (answeredCount / totalCount) * 100;
  const canSubmit = answeredCount === totalCount;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Get companyId from window
      const companyId = (window as any).companyID;
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Convert answers to SOAAnswer format
      const soaAnswers: SOAAnswer[] = Object.entries(answers)
        .filter(([_, answer]) => answer !== null)
        .map(([questionId, answer]) => ({
          questionId,
          controlId: QUESTION_TO_CONTROL_MAP[questionId] || questionId,
          answer: answer as 'yes' | 'no' | 'unsure'
        }));
      
      const result = await completePrivacyActSOA(companyId, soaAnswers);
      
      setSuccess({ controlsApplicable: result.controlsApplicable, controlsNotApplicable: result.controlsNotApplicable, tasksCreated: result.tasksCreated, tasksSkipped: result.tasksSkipped });
      
      // Mark the onboarding step as done
      setStatus('control_selection', 'done');
    } catch (err) {
      console.error('Error submitting SOA:', err);
      setError('Failed to save your responses. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setOpen(false);
      closeActive();
      openStep('assurance_task_scheduling');
    }
  };

  const handleBack = () => {
    if (!submitting) {
      setOpen(false);
      goBack();
    }
  };

  // Success state
  if (success) {
    return (
      <div className="space-y-4">
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent className="max-w-lg bg-card/95 backdrop-blur-xl border-white/10 p-0">
            <div className="p-8 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Questionnaire Completed</h2>
              <p className="text-muted-foreground mb-6">
                Your answers have updated your de.iterate Control Centre for selected controls in place within your business. You can change these later in your settings.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Controls applicable</span>
                  <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-600">
                    {success.controlsApplicable}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Controls not applicable</span>
                  <Badge variant="secondary" className="bg-muted text-muted-foreground">
                    {success.controlsNotApplicable}
                  </Badge>
                </div>
              </div>
              <Button onClick={handleClose} className="w-full">
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) { setOpen(false); closeActive(); } }}>
        <DialogContent className="max-w-6xl! w-[90vw]! h-[90vh]! bg-transparent shadow-none border-0 p-0 overflow-hidden!">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full h-full shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => { if (!submitting) { setOpen(false); closeActive(); } }}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
              disabled={submitting}
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Header - Fixed */}
            <div className="shrink-0 p-6 pb-4 border-b border-white/10 bg-card/95 backdrop-blur-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">Privacy Act Questionnaire</h2>
                  <p className="text-sm text-muted-foreground">
                    Answer these questions to build your Compliance Program
                  </p>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">{answeredCount} of {totalCount} questions answered</span>
                  <span className="text-muted-foreground">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : error && !Object.keys(controls).length ? (
                <div className="text-center py-12">
                  <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-500">{error}</p>
                  <Button variant="outline" onClick={() => window.location.reload()} className="mt-4">
                    Retry
                  </Button>
                </div>
              ) : (
                PRIVACY_ACT_CATEGORIES.map((category, index) => {
                  const answer = answers[category.id];
                  const controlId = QUESTION_TO_CONTROL_MAP[category.id];
                  const control = controls[controlId];
                  
                  return (
                    <div 
                      key={category.id}
                      className={cn(
                        "rounded-xl border bg-white/5 backdrop-blur-sm p-5 transition-all",
                        answer ? "border-white/20" : "border-white/10"
                      )}
                    >
                      <div className="flex items-start gap-4">
                        {/* Question number */}
                        <div className={cn(
                          "shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium",
                          answer ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                        )}>
                          {answer ? getAnswerIcon(answer) : index + 1}
                        </div>
                        
                        {/* Question content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div>
                              <Badge variant="outline" className="mb-2 text-xs">
                                {category.shortHeading}
                              </Badge>
                              <h3 className="font-medium text-foreground leading-relaxed">
                                {category.question.text}
                              </h3>
                            </div>
                          </div>
                          
                          {/* Control info */}
                          {control && (
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                              {control.controlDescription}
                            </p>
                          )}
                          
                          {/* Answer buttons */}
                          <div className="flex items-center gap-2 mt-4">
                            <Button
                              variant={answer === 'yes' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleAnswer(category.id, 'yes')}
                              className={cn(
                                "gap-2 transition-all",
                                answer === 'yes' 
                                  ? "bg-emerald-600 hover:bg-emerald-700 border-emerald-600" 
                                  : "hover:border-emerald-500 hover:text-emerald-500 hover:bg-emerald-500/10"
                              )}
                            >
                              <Check className="h-4 w-4" />
                              Yes
                            </Button>
                            
                            <Button
                              variant={answer === 'no' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleAnswer(category.id, 'no')}
                              className={cn(
                                "gap-2 transition-all",
                                answer === 'no' 
                                  ? "bg-red-600 hover:bg-red-700 border-red-600" 
                                  : "hover:border-red-500 hover:text-red-500 hover:bg-red-500/10"
                              )}
                            >
                              <X className="h-4 w-4" />
                              No
                            </Button>
                            
                            <Button
                              variant={answer === 'unsure' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleAnswer(category.id, 'unsure')}
                              className={cn(
                                "gap-2 transition-all",
                                answer === 'unsure' 
                                  ? "bg-amber-600 hover:bg-amber-700 border-amber-600" 
                                  : "hover:border-amber-500 hover:text-amber-500 hover:bg-amber-500/10"
                              )}
                            >
                              <HelpCircle className="h-4 w-4" />
                              Unsure
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer - Sticky */}
            <div className="sticky bottom-0 z-10 border-t border-white/10 px-6 py-4 bg-card/95 backdrop-blur-xl bg-linear-to-t from-primary/5 to-transparent">
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {canSubmit ? (
                    <span className="text-emerald-500 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      All questions answered
                    </span>
                  ) : (
                    <span>Answer all questions to continue</span>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleBack}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={!canSubmit || submitting}
                    className="gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Save and Next
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
